import { reportHandledException } from '@/lib/error-reporting'
import { isClarityEnabled, isGaEnabled, isPixelEnabled } from './config'
import { analyticsPlatformsAllowedAt, type AnalyticsPlatforms } from './excluded-paths'

/**
 * Crossing into a page that must not carry a third-party script.
 *
 * `<AnalyticsScripts>` renders nothing on such a page, but that only helps a
 * page LOADED there. GA, Pixel and Clarity install once per document and keep
 * running across client-side navigation: Clarity has no documented way to stop
 * or pause recording (its client API is `consent`, `identify`, `set`, `event`,
 * `upgrade`; `consent(false)` only clears cookies), and GA4 can report the new
 * URL on its own when the history changes. So a soft navigation from a page
 * that loaded a script into one that forbids it carries the script along.
 *
 * The only reliable exit is a real document load at the new URL, where the
 * loader decides from scratch. This module decides when that is needed and
 * performs it, for the three ways an in-site navigation starts:
 *
 * - a click on a same-origin link (`next/link` included): `installScriptBoundaryGuard`
 * - back/forward: `installScriptBoundaryGuard`
 * - code that navigates by itself (`router.push` after sign-in and the like):
 *   `navigateWithinSite`
 */

type AnalyticsGlobals = Window & { gtag?: unknown; fbq?: unknown; clarity?: unknown }

const NOTHING_RUNNING: AnalyticsPlatforms = { ga: false, pixel: false, clarity: false }

/** Scripts this document has actually installed: each loader defines its global first. */
function installedPlatforms(): AnalyticsPlatforms {
  const globals = window as AnalyticsGlobals
  return {
    ga: typeof globals.gtag === 'function',
    pixel: typeof globals.fbq === 'function',
    clarity: typeof globals.clarity === 'function',
  }
}

/**
 * Scripts that may be running while the visitor is on `url`: whatever is
 * installed, plus whatever the page at `url` loads. The second half covers the
 * window between render and load — Clarity is `lazyOnload`, so a click in the
 * first moments of a visit would otherwise look script-free and let the
 * pending load finish on the next page.
 */
function platformsRunningOn(url: URL | null): AnalyticsPlatforms {
  const installed = installedPlatforms()
  const allowed = url ? analyticsPlatformsAllowedAt(url.pathname, url.search) : NOTHING_RUNNING
  return {
    ga: installed.ga || (allowed.ga && isGaEnabled()),
    pixel: installed.pixel || (allowed.pixel && isPixelEnabled()),
    clarity: installed.clarity || (allowed.clarity && isClarityEnabled()),
  }
}

function carriesForbiddenScript(running: AnalyticsPlatforms, target: URL): boolean {
  const allowed = analyticsPlatformsAllowedAt(target.pathname, target.search)
  return (
    (running.ga && !allowed.ga) ||
    (running.pixel && !allowed.pixel) ||
    (running.clarity && !allowed.clarity)
  )
}

function toSameOriginUrl(href: string): URL | null {
  try {
    const url = new URL(href, window.location.href)
    return url.origin === window.location.origin ? url : null
  } catch (error) {
    // Not a URL the browser can load either, so there is no navigation to guard.
    reportHandledException(error, { source: 'lib/analytics/script-boundary.ts' })
    return null
  }
}

/** Whether going from the current page to `href` in this document would carry a script `href` forbids. */
export function needsFullPageLoad(href: string): boolean {
  if (typeof window === 'undefined') return false
  const target = toSameOriginUrl(href)
  if (!target) return false
  const here = new URL(window.location.href)
  return carriesForbiddenScript(platformsRunningOn(here), target)
}

/**
 * `router.push` / `router.replace` for code that navigates on its own. Soft
 * navigation when the destination may carry what this page runs; otherwise a
 * real load, which is the only way to leave the scripts behind.
 */
export function navigateWithinSite(
  href: string,
  softNavigate: () => void,
  options: { replace?: boolean; location?: Pick<Location, 'assign' | 'replace'> } = {}
): void {
  if (!needsFullPageLoad(href)) {
    softNavigate()
    return
  }
  const location = options.location ?? window.location
  if (options.replace) location.replace(href)
  else location.assign(href)
}

/**
 * Something on the page that asks the user before letting them leave (an
 * editor with unsaved changes). While one says it is asking, the guard stands
 * back: the question comes first, and the leaving it approves reaches the
 * guard again as a fresh click or history step, which is when the guard
 * decides HOW to leave.
 */
type NavigationConfirmer = () => boolean

const confirmers = new Set<NavigationConfirmer>()

/**
 * Registers `wouldAsk`, which answers "would you stop this navigation to ask
 * the user right now?". Returns the unregister function.
 */
export function registerNavigationConfirmer(wouldAsk: NavigationConfirmer): () => void {
  confirmers.add(wouldAsk)
  return () => {
    confirmers.delete(wouldAsk)
  }
}

const someoneWillAsk = (): boolean => [...confirmers].some((wouldAsk) => wouldAsk())

/** The page being left: its URL and the history state the router keeps for it. */
export interface LeftPage {
  url: string
  state: unknown
}

interface GuardDeps {
  assign: (href: string) => void
  replace: (href: string) => void
}

const defaultDeps = (): GuardDeps => ({
  assign: (href) => window.location.assign(href),
  replace: (href) => window.location.replace(href),
})

/**
 * Listens for the navigations nothing else in the app routes through
 * `navigateWithinSite`: link clicks and back/forward. Returns the cleanup.
 *
 * Link clicks are taken in the CAPTURE phase on `document`, which runs before
 * React's own handlers on the root. Cancelling there is what `next/link`
 * checks before routing (`if (e.defaultPrevented) return` in
 * `next/dist/client/app-dir/link.js`), so the soft navigation never starts and
 * the page is loaded for real instead.
 *
 * Back/forward is taken in the capture phase on `window`, which fires before
 * the App Router's own `popstate` listener, registered without capture, and
 * before any analytics script that reports history changes. By then the
 * address bar already shows the destination while this document still runs the
 * scripts it forbids, so the entry is first put back to the page being left
 * (`history.replaceState` with that page's URL and router state) and only then
 * is the destination loaded for real, with `location.replace` so it loads INTO
 * that same entry: the session history keeps its length and order, and the
 * forward entries survive (`location.assign` would push a new entry, leaving
 * a duplicate of the page being left behind it and cutting everything
 * forward). If that load is cancelled (a `beforeunload` prompt the user
 * declines), the page, its URL and its scripts are consistent again: nothing
 * is left reporting the destination's URL.
 * `leftPage` is updated after every navigation by the component that installs
 * this.
 *
 * Both give way to a registered confirmer that is about to ask the user.
 */
export function installScriptBoundaryGuard(
  leftPage: () => LeftPage,
  deps: GuardDeps = defaultDeps()
): () => void {
  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null
    if (!(anchor instanceof HTMLAnchorElement)) return
    if (anchor.target && anchor.target !== '_self') return
    if (anchor.hasAttribute('download')) return
    const target = toSameOriginUrl(anchor.href)
    if (!target) return
    const here = new URL(window.location.href)
    // Same document, different fragment: no navigation happens.
    if (target.pathname === here.pathname && target.search === here.search) return
    if (!carriesForbiddenScript(platformsRunningOn(here), target)) return
    if (someoneWillAsk()) return
    event.preventDefault()
    deps.assign(target.href)
  }

  const onPopState = (event: PopStateEvent) => {
    const target = new URL(window.location.href)
    const left = leftPage()
    if (!carriesForbiddenScript(platformsRunningOn(toSameOriginUrl(left.url)), target)) return
    if (someoneWillAsk()) return
    event.stopImmediatePropagation()
    window.history.replaceState(left.state, '', left.url)
    deps.replace(target.href)
  }

  document.addEventListener('click', onClick, true)
  window.addEventListener('popstate', onPopState, true)
  return () => {
    document.removeEventListener('click', onClick, true)
    window.removeEventListener('popstate', onPopState, true)
  }
}
